import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRightIcon } from 'lucide-react';
import { MaskText, SectionLabel } from './ui/Reveal';
import { MagneticButton } from './ui/MagneticButton';
import { links, projects } from '../data/site';
import { useHasFinePointer, usePrefersReducedMotion } from '../hooks/useMedia';
const filters = ['All', 'Branding', 'Logo Design', 'Motion', 'Social Media', 'Packaging', 'Presentation', 'Digital', 'Apparel', 'Other'];
type Props = {
  /** Show the section heading + supporting copy. */
  withHeading?: boolean;
  /** Enable the category filter row. */
  withFilters?: boolean;
  onSelect?: (slug: string) => void;
};

/**
 * Editorial portfolio grid. Real projects only — the grid scales as
 * new entries are added to `projects` in data/site.ts.
 */
export function PortfolioGrid({
  withHeading = true,
  withFilters = false,
  onSelect
}: Props) {
  const [filter, setFilter] = useState('All');
  const [hovered, setHovered] = useState<string | null>(null);
  const [pointer, setPointer] = useState({
    x: 0,
    y: 0
  });
  const fine = useHasFinePointer();
  const reduced = usePrefersReducedMotion();
  const navigate = useNavigate();
  const visible = useMemo(() => filter === 'All' ? projects : projects.filter(p => p.categories.includes(filter)), [filter]);
  const activeCounts = useMemo(() => {
    const counts: Record<string, number> = {
      All: projects.length
    };
    projects.forEach(project => {
      project.categories.forEach(category => {
        counts[category] = (counts[category] ?? 0) + 1;
      });
    });
    return counts;
  }, []);
  return <section className="bg-surface py-20 sm:py-28" aria-label="Selected work" data-id="element-401" data-mp="1njsdni5a3exn">
      <div className="mx-auto w-full max-w-container px-5 sm:px-8" data-id="element-402" data-mp="18kyevy8kaxqn">
        {withHeading ? <div className="grid gap-6 lg:grid-cols-[1fr_1fr] lg:items-end" data-id="element-403" data-mp="1dl2w5qhanwhx">
            <div data-id="element-404" data-mp="1dme29wy7pgc7">
              <SectionLabel data-id="element-405" data-mp="ufoduj90yfxn">Portfolio</SectionLabel>
              <h2 className="mt-5 font-display text-5xl font-extrabold uppercase leading-[0.9] tracking-tightest sm:text-7xl" data-id="element-406" data-mp="uh94hxs7xeq8">
                <MaskText text="Selected|Work" data-id="element-407" data-mp="kq4qb0ad9xwg" />
              </h2>
            </div>
            <p className="max-w-md text-base leading-relaxed text-muted sm:text-lg" data-id="element-408" data-mp="ko0rgwrbrp31">
              A selection of recent work created to solve problems, shape perceptions and bring ideas
              to life. Most client work stays private — the full archive lives off-site.
            </p>
          </div> : null}

        {withFilters ? <div className="mt-12 flex flex-wrap gap-2" role="group" aria-label="Filter work by category" data-id="element-409" data-mp="13ftfecrjek3w">
            {filters.map(item => {
          const count = activeCounts[item] ?? 0;
          const isActive = filter === item;
          return <button key={item} type="button" onClick={() => setFilter(item)} aria-pressed={isActive} disabled={count === 0} className={`label border px-3.5 py-2 transition-colors ${isActive ? 'border-brand bg-brand text-white' : count === 0 ? 'border-line text-muted/50' : 'border-line text-muted hover:border-brand hover:text-brand'}`} data-id="element-410" data-mp="3j7qw3e105njy">
                  {item}
                  <span className="ml-2 opacity-60" data-id="element-411" data-mp="2mej0f9wtokko">{count}</span>
                </button>;
        })}
          </div> : null}

        <div className="relative mt-12" onMouseMove={event => setPointer({
        x: event.clientX,
        y: event.clientY
      })} data-id="element-412" data-mp="2rcark1pn82nt">
          {visible.length === 0 ? <p className="border border-dashed border-line px-6 py-16 text-center text-sm text-muted" data-id="element-413" data-mp="x274tii976y4">
              Nothing published in this category yet. More work is released as clients approve it.
            </p> : <ul className="grid gap-10 lg:grid-cols-2" data-id="element-414" data-mp="2nhgfcv50odoe">
              {visible.map(project => <li key={project.slug} data-id="element-415" data-mp="31h962qgjmtwd">
                  <button type="button" onClick={() => onSelect ? onSelect(project.slug) : navigate('/work')} onMouseEnter={() => setHovered(project.slug)} onMouseLeave={() => setHovered(null)} className="group block w-full text-left" data-cursor="label" data-cursor-label="View project" data-id="element-416" data-mp="31fxzkivpr81k">
                    <div className="relative overflow-hidden bg-surface2" data-id="element-417" data-mp="2dqlsppnuzc2f">
                      <img src={project.cover} alt={`${project.title} — ${project.subtitle}`} className="aspect-[4/3] w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05]" loading="lazy" decoding="async" data-id="element-418" data-mp="1q1kflp42zcqt" />
                      <div className="absolute inset-0 bg-brand/0 transition-colors duration-500 group-hover:bg-brand/10" aria-hidden="true" data-id="element-419" data-mp="11z5ohz7kufbq" />
                    </div>
                    <div className="mt-4 flex items-start justify-between gap-6 border-t border-line pt-4" data-id="element-420" data-mp="1unte26zczqv3">
                      <div data-id="element-421" data-mp="1zk2084bz4kbr">
                        <h3 className="font-display text-2xl font-bold uppercase leading-none tracking-tightest transition-colors group-hover:text-brand sm:text-3xl" data-id="element-422" data-mp="1kp46p8539ot3">
                          {project.title}
                        </h3>
                        <p className="mt-2 text-sm text-muted" data-id="element-423" data-mp="98xeiun73188">{project.subtitle}</p>
                      </div>
                      <span className="label flex shrink-0 items-center gap-2 text-muted" data-id="element-424" data-mp="eacz6llgn5q3">
                        {project.year}
                        <ArrowUpRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-425" data-mp="2gx9wjpbzfw86" />
                      </span>
                    </div>
                  </button>
                </li>)}
            </ul>}

          {/* Cursor-following preview (desktop only) */}
          {fine && !reduced ? <AnimatePresence data-id="element-426" data-mp="1x77zo5jdzg3b">
              {hovered ? <motion.div className="pointer-events-none fixed z-[60] hidden h-40 w-32 overflow-hidden lg:block" style={{
            left: pointer.x + 28,
            top: pointer.y - 80
          }} initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} transition={{
            duration: 0.25
          }} aria-hidden="true" data-id="element-427" data-mp="1x8stfsxvmv04">
                  <img src={projects.find(p => p.slug === hovered)?.gallery[0]?.src} alt="" className="h-full w-full object-cover" data-id="element-428" data-mp="3dmr8un5h0giu" />
                </motion.div> : null}
            </AnimatePresence> : null}
        </div>

        <div className="mt-14 flex flex-col items-start gap-4 border-t border-line pt-8 sm:flex-row sm:items-center sm:justify-between" data-id="element-429" data-mp="3uvtvrmxoj4es">
          <p className="max-w-md text-sm text-muted" data-id="element-430" data-mp="326mmbswosuer">
            Client confidentiality keeps most of the archive private. The full portfolio is available
            on request and through our portfolio channel.
          </p>
          <MagneticButton href={links.portfolio} cursorLabel="View portfolio" showArrow data-id="element-431" data-mp="3c3iyq9ppi0ol">
            View All Portfolio
          </MagneticButton>
        </div>
      </div>
    </section>;
}